// Pace against your best run on a realm and Storm Tier: that run's kills every PACE_STEP seconds, so the HUD can
// say how far ahead or behind you are at the same moment.
// shortcut: kept in this device's localStorage, not the cloud save; sync it if players ask for it across devices.
export const PACE_STEP = 30;
const KEY = 'mana-storm-pace-v1';

export interface PaceRun {
  victory: boolean;
  kills: number;
  time: number;
  marks: number[]; // marks[k]: kills at (k + 1) * PACE_STEP seconds
}

// The best run's kills at time t, linear between its marks; null once t is past its last mark.
export function paceAt(marks: readonly number[], t: number): number | null {
  const at = t / PACE_STEP;
  if (marks.length === 0 || at > marks.length) return null;
  const seg = Math.min(Math.floor(at), marks.length - 1);
  const from = seg === 0 ? 0 : marks[seg - 1];
  return from + (marks[seg] - from) * (at - seg);
}

// The leaderboard's order: a victory, then more kills, then a faster time.
export function betterRun(a: PaceRun, b: PaceRun): boolean {
  if (a.victory !== b.victory) return a.victory;
  if (a.kills !== b.kills) return a.kills > b.kills;
  return a.time < b.time;
}

function readAll(): Record<string, PaceRun> {
  try {
    const all = JSON.parse(localStorage.getItem(KEY) ?? '{}') as unknown;
    return all && typeof all === 'object' ? (all as Record<string, PaceRun>) : {};
  } catch {
    return {};
  }
}

export function loadPace(key: string): PaceRun | null {
  const run = readAll()[key];
  return run && Array.isArray(run.marks) && run.marks.every((n) => typeof n === 'number') ? run : null;
}

// Stores the run if it beats this device's best for the key; returns whether it did.
export function recordPace(key: string, run: PaceRun): boolean {
  const all = readAll();
  const prev = all[key];
  if (prev && Array.isArray(prev.marks) && !betterRun(run, prev)) return false;
  all[key] = { victory: run.victory, kills: run.kills, time: run.time, marks: run.marks.slice() };
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    // storage full or blocked: no pace next time, nothing else breaks
  }
  return true;
}

// Sign-out: the next player on this device starts without someone else's pace.
export function clearPace(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // storage blocked: nothing was stored either
  }
}
